import type { AreaCode } from "@/types/areaCode";

export function normalizeSearchInput(input: string) {
  return input.trim().toLowerCase().replace(/\s+/g, " ");
}

export function compactSearchInput(input: string) {
  return normalizeSearchInput(input).replace(/[^a-z0-9]/g, "");
}

export function extractAreaCodeFromPhone(input: string) {
  const digits = input.replace(/\D/g, "");

  if (/^\d{3}$/.test(digits)) {
    return digits;
  }

  if (digits.length === 10) {
    return digits.slice(0, 3);
  }

  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1, 4);
  }

  return null;
}

function recordHaystack(areaCode: AreaCode) {
  return [
    areaCode.code,
    areaCode.region,
    areaCode.regionAbbr,
    areaCode.country,
    areaCode.primaryCity,
    ...areaCode.cities,
    ...(areaCode.overlays ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function matchesCityAndRegion(areaCode: AreaCode, city: string, region: string) {
  const cityTerm = compactSearchInput(city);
  const regionTerm = compactSearchInput(region);
  const cityMatches =
    areaCode.cities.some((candidate) => compactSearchInput(candidate).includes(cityTerm)) ||
    compactSearchInput(areaCode.primaryCity ?? "").includes(cityTerm) ||
    compactSearchInput(areaCode.region).includes(cityTerm);
  const regionMatches = [areaCode.region, areaCode.regionAbbr, areaCode.country]
    .filter(Boolean)
    .some((candidate) => compactSearchInput(candidate ?? "").includes(regionTerm));

  return cityMatches && regionMatches;
}

export function searchByCityOrRegion(input: string, records: AreaCode[]) {
  const query = normalizeSearchInput(input);
  const compactQuery = compactSearchInput(query);

  if (!compactQuery) {
    return [];
  }

  if (query.includes(",")) {
    const [city, region] = query.split(",", 2).map((part) => part.trim());
    if (city && region) {
      return records.filter((areaCode) => matchesCityAndRegion(areaCode, city, region));
    }
  }

  return records.filter((areaCode) => {
    const searchable = compactSearchInput(recordHaystack(areaCode));
    return searchable.includes(compactQuery);
  });
}

export function searchAreaCodes(input: string, records: AreaCode[]) {
  const code = extractAreaCodeFromPhone(input);
  if (code) {
    const codeMatch = records.filter((areaCode) => areaCode.code === code);
    if (codeMatch.length > 0) {
      return codeMatch;
    }
  }

  return searchByCityOrRegion(input, records);
}
